import * as React from "react";
import {
    getRuleFails,
    ruleFailConfirmFixed,
    ruleFailMarkUnknown,
    ruleRun,
} from "./rulesService";
import Loading from "../../components/loading";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogTitle,
    FormControl,
    FormGroup,
    IconButton,
    Input,
    InputAdornment,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Tooltip,
    Box,
} from "@mui/material";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import HistoryIcon from "@mui/icons-material/History";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SchoolIcon from "@mui/icons-material/School";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import {Link as RouterLink} from "react-router-dom";
import {toast} from "react-toastify";
import moment from "moment";

const oldUrl = process.env.REACT_APP_OLD_URL;

function rowMatchesSearch(row: any, search: string): boolean {
    if (!search) return true;
    const s = search.toLowerCase();
    return Object.values(row).some((v) => v != null && String(v).toLowerCase().includes(s));
}

function rowKey(row: any) {
    return `${row.lead_id}-${row.hp_campaign_id}`;
}

export default function RuleFailsPage() {
    const [results, setResults] = React.useState([] as any[]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');
    const [markConfirm, setMarkConfirm] = React.useState<null | {row: any, ruleActionId: number}>(null);
    const [actionLoading, setActionLoading] = React.useState(false);

    React.useEffect(() => {
        document.title = "Bad Confirms | DN";
    }, []);

    const reload = async () => {
        setLoading(true);
        const data = await getRuleFails();
        setResults(data);
        setLoading(false);
    };

    React.useEffect(() => {
        reload().then(() => {});
    }, []);

    const removeRow = (row: any) => {
        setResults((prev) => prev.filter((x) => !(x.lead_id === row.lead_id && x.hp_campaign_id === row.hp_campaign_id)));
    };

    const handleConfirmFixed = async (row: any) => {
        const ok = await ruleFailConfirmFixed(row.lead_id, row.hp_campaign_id);
        if (ok) {
            removeRow(row);
        } else {
            toast.error('Unable to confirm fix, please try again.');
        }
    };

    const handleRerun = async (row: any) => {
        const ok = await ruleRun(row.lead_id, row.hp_campaign_id, row.confirm);
        if (ok) {
            toast.success(`Rules ran on lead_id ${row.lead_id}`);
            await reload();
        } else {
            toast.error('Unable to re-run rules, please try again.');
        }
    };

    const handleMark = async () => {
        if (!markConfirm) return;
        setActionLoading(true);
        const {row, ruleActionId} = markConfirm;
        const ok = await ruleFailMarkUnknown(row.lead_id, row.hp_campaign_id, ruleActionId);
        setActionLoading(false);
        if (ok) {
            setMarkConfirm(null);
            removeRow(row);
        } else {
            toast.error('Unable to mark, please try again.');
        }
    };

    const filtered = results.filter((row) => rowMatchesSearch(row, search));

    return (
        <>
            <h2>Bad Confirms</h2>
            <FormGroup row={true}>
                <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }} size="small">
                    <Input
                        id="search"
                        type="text"
                        spellCheck="false"
                        placeholder="Search"
                        value={search || ''}
                        onChange={(e: any) => setSearch(e.target.value)}
                        startAdornment={<InputAdornment position="start"><SearchIcon /></InputAdornment>}
                    />
                </FormControl>
            </FormGroup>
            {loading ? <Loading/> :
                <>
                    {!filtered.length ? <Alert severity="warning">No results found</Alert> :
                        <>
                            <Box sx={{margin: '10px 0', fontWeight: 'bold', fontSize: '13px'}}>
                                Showing {filtered.length.toLocaleString()} of {results.length.toLocaleString()}
                            </Box>
                            <TableContainer component={Paper} sx={{width: '100%', maxWidth: '100%', overflowX: 'auto'}}>
                                <Table size="small" sx={{tableLayout: 'fixed', width: '100%'}}>
                                    <TableHead>
                                        <TableHeaderRow>
                                            <TableCell sx={{width: '9%'}}>Date</TableCell>
                                            <TableCell sx={{width: '10%'}}>Company</TableCell>
                                            <TableCell sx={{width: '9%'}}>Broker</TableCell>
                                            <TableCell sx={{width: '10%'}}>School</TableCell>
                                            <TableCell sx={{width: '12%'}}>App Id</TableCell>
                                            <TableCell sx={{width: '7%'}}>Campaign</TableCell>
                                            <TableCell sx={{width: '6%'}}>Lead</TableCell>
                                            <TableCell sx={{width: '8%'}}>Action</TableCell>
                                            <TableCell sx={{width: '18%'}}>Confirm</TableCell>
                                            <TableCell sx={{width: '11%'}}></TableCell>
                                        </TableHeaderRow>
                                    </TableHead>
                                    <TableBody>
                                        {filtered.map((row: any) => (
                                            <StripedTableRow key={rowKey(row)}>
                                                <TableCell sx={{whiteSpace: 'nowrap'}}>
                                                    {moment(row.date_sent).format('M/D/YYYY, h:mm A')}
                                                </TableCell>
                                                <TableCell>{row.company_name}</TableCell>
                                                <TableCell>{row.broker_name}</TableCell>
                                                <TableCell>{row.school_name}</TableCell>
                                                <TableCell>
                                                    {row.app_id}
                                                    <Box component="span" sx={{display: 'inline-flex', gap: 0.5, ml: 0.5, verticalAlign: 'middle'}}>
                                                        {row.landing_page && (
                                                            <Tooltip title="Preview Link">
                                                                <IconButton
                                                                    size="small"
                                                                    component="a"
                                                                    href={`${row.landing_page}?V1294=4581167&V1394=85040&V1894=${row.portal_redirect_id}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <OpenInNewIcon sx={{fontSize: 16}} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="See Confirms">
                                                            <IconButton
                                                                size="small"
                                                                component={RouterLink}
                                                                to={`/campaignConfirms/${row.hp_campaign_id}`}
                                                                target="_blank"
                                                            >
                                                                <CheckCircleOutlineIcon sx={{fontSize: 16}} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Manage PM">
                                                            <IconButton
                                                                size="small"
                                                                component={RouterLink}
                                                                to={`/programSets/${row.hp_campus_search_id}`}
                                                                target="_blank"
                                                            >
                                                                <SchoolIcon sx={{fontSize: 16}} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Link
                                                        href={`${oldUrl}/hp_campaign_update.php?hp_campaign_id=${row.hp_campaign_id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {row.hp_campaign_id}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>
                                                    <Link
                                                        href={`${oldUrl}/lead_display.php?email=${encodeURIComponent(row.email)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {row.lead_id}
                                                    </Link>
                                                </TableCell>
                                                <TableCell sx={{wordBreak: 'break-word'}}>{row.action}</TableCell>
                                                <TableCell sx={{wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'normal'}}>{row.confirm}</TableCell>
                                                <TableCell sx={{whiteSpace: 'nowrap'}}>
                                                    <Tooltip title="Confirm Fixed">
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => handleConfirmFixed(row)}
                                                        >
                                                            <CheckIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Re-run Rules">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleRerun(row)}
                                                        >
                                                            <HistoryIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {row.action === 'Unknown' && (
                                                        <>
                                                            <Tooltip title="Mark as Good">
                                                                <IconButton
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => setMarkConfirm({row, ruleActionId: 1})}
                                                                >
                                                                    <SentimentSatisfiedAltIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Mark as Bad">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => setMarkConfirm({row, ruleActionId: 14})}
                                                                >
                                                                    <SentimentDissatisfiedIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </TableCell>
                                            </StripedTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    }
                </>
            }

            <Dialog
                open={!!markConfirm}
                onClose={() => { if (!actionLoading) setMarkConfirm(null); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {`Are you sure you want to mark this Unknown as ${markConfirm?.ruleActionId === 1 ? 'Good' : 'Bad'}?`}
                </DialogTitle>
                <DialogActions>
                    <Button sx={{color: 'green'}} disabled={actionLoading} onClick={() => setMarkConfirm(null)}>Cancel</Button>
                    <Button sx={{color: 'red'}} disabled={actionLoading} onClick={handleMark} autoFocus>Confirm</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
